import { Component, inject } from '@angular/core';
import { RouterModule } from '@angular/router';
import { SpikeTitlebarComponent } from './_spike/shell/titlebar/spike-titlebar.component';
import { NavRailComponent } from './layout/nav-rail/nav-rail.component';
import { SpikeFooterComponent } from './_spike/shell/footer/spike-footer.component';
import { HlmToastContainerComponent } from './ui/toast.component';
import { ToastService } from './ui/toast.service';
import { KeyboardService } from './core/keyboard/keyboard.service';
import { GlobalSearchService } from './shared/global-search/global-search.service';
import { PageFlipService } from './core/services/page-flip.service';
import { LayoutWatchdogService } from './core/services/layout-watchdog.service';
import { PenguinOverlayComponent } from './shared/penguin/penguin-overlay.component';
import { AuthService } from './core/auth/auth.service';
import { VersionService } from './core/version/version.service';
import { UsageTrackingService } from './core/services/usage-tracking.service';
import { AccessDeniedComponent } from './features/access-denied/access-denied.component';
import { HlmSpinnerComponent } from './ui/misc';

@Component({
  imports: [
    RouterModule,
    NavRailComponent,
    SpikeTitlebarComponent,
    SpikeFooterComponent,
    HlmToastContainerComponent,
    PenguinOverlayComponent,
    AccessDeniedComponent,
    HlmSpinnerComponent,
  ],
  selector: 'cmt-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App {
  // EB-3775 — root shell @switch on access state.
  readonly accessState = inject(AuthService).accessState;

  constructor() {
    inject(KeyboardService).init();
    inject(GlobalSearchService).init();
    inject(PageFlipService).init();

    // EB-3796: kick off /version.json polling AFTER the auth APP_INITIALIZER
    // has resolved (App is only constructed once bootstrap completes). The
    // service is a no-op on local dev (appVersion === '0.0.0-local').
    inject(VersionService).start();

    // EB-3798: begin usage tracking (app.open once + section.open on
    // navigation) — but ONLY for an authorized session. App is the root shell
    // and is constructed even when access is denied (it @switches on the state
    // to render AccessDenied), so an unguarded start() would emit app.open for a
    // denied user AND race the /ssologout that clears the token → a token-less
    // 401 POST. (PR #43 review.)
    if (this.accessState() === 'authorized') {
      inject(UsageTrackingService).start();
    }

    // Defence-in-depth against runaway layout-loops (see EB-3642 retro).
    // Surfaces a single toast + console warning the first time the watchdog
    // engages emergency mode — global transitions are killed via CSS so the
    // page becomes usable again without a forced reload.
    const watchdog = inject(LayoutWatchdogService);
    const toast = inject(ToastService);
    watchdog.onEmergency = (reason) => {
      toast.open(
        `Layout instability detected (${reason}). Animations paused — please refresh if the page still feels unstable.`,
        'OK',
        { duration: 10000, tone: 'default' },
      );
    };
    watchdog.start();
  }
}
